import React, { useState } from 'react';
import { Slider } from '@wajeez-web/components/ui';
import { Skeleton } from 'antd';
import { MediaHelpers } from '@wajeez-web/core/api';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { ClickableComponentLayout } from '../layouts/ClickableComponentLayout';

export interface CategoriesLargeListParameters {
  title: string | null;
  seeAllTitle: string | null;
}

export function CategoriesLargeList({
  component,
  context,
  isLastSibling,
}: BaseComponentProps) {
  const [pageNumber, setPageNumber] = useState(1);

  const { getParameterValue, contents, contentsRequestStatus, meta } =
    useComponentData<CategoriesLargeListParameters>(
      component,
      context,
      pageNumber
    );

  const { isActive } = useSkeletonHelper(
    contentsRequestStatus,
    contents?.length ? true : false
  );

  const { lastElementRef } = useInfiniteScroll(
    meta?.hasNextPage,
    setPageNumber
  );

  const title = getParameterValue('title');

  const isContentOrLoading = contents?.length;

  return isContentOrLoading ? (
    <div className="space-y-[17px] sm:space-y-[7px]">
      <div className="flex justify-between px-4 sm:px-0">
        <div className="font-bold text-[20px] sm:text-[18px] sm:leading-[30px] sm:text-[#27160E]">
          {title}
        </div>
      </div>
      <div>
        <div
          className={`w-full sm:pb-[28px] ${
            !isLastSibling && 'border-b-[0.5px]'
          } border-[#C6C6C8] dark:border-dark-divider dark:border-dark-divider`}
        >
          <Slider
            arrows={false}
            dots={true}
            itemClass="!w-auto"
            customStyle="pb-10 sm:pb-0"
            responsive={{
              desktop: {
                breakpoint: {
                  max: 3000,
                  min: 1362,
                },
                items: 5,
              },
              miniDesktop: {
                breakpoint: {
                  max: 1362,
                  min: 1024,
                },
                items: 4,
              },
              mobile: {
                breakpoint: {
                  max: 464,
                  min: 0,
                },
                items: 1,
              },
              tablet: {
                breakpoint: {
                  max: 1024,
                  min: 464,
                },
                items: 2,
              },
            }}
          >
            {contents?.map((content, i) => {
              return (
                <RepeatedCuratedListGridItem
                  key={content.id}
                  component={{ ...component?.itemTemplate, uniqueId: i }}
                  context={content}
                  ref={contents?.length - 1 === i ? lastElementRef : null}
                />
              );
            })}
          </Slider>
        </div>
      </div>
    </div>
  ) : contentsRequestStatus === 'loading' && !contents?.length ? (
    <CategoriesLargeListSkeleton
      isLastSibling={isLastSibling}
      isActive={isActive}
    />
  ) : null;
}

export const CategoriesLargeListSkeleton = ({
  isLastSibling,
  isActive = true,
  hasTitle = true,
  sliderCustomStyle,
  shape = 'round',
}: {
  isLastSibling: boolean | 0 | undefined;
  isActive?: boolean;
  hasTitle?: boolean;
  sliderCustomStyle?: string;
  shape?: 'circle' | 'round' | 'square' | 'default' | undefined;
}) => {
  return (
    <div className="w-full pr-2 sm:pr-0">
      {hasTitle && (
        <div className="pr-2 sm:pr-0">
          <Skeleton.Button
            active={isActive}
            shape="square"
            className="!w-[200px] !h-[30px] dark:bg-dark-medium"
          />
        </div>
      )}
      <div
        className={`${hasTitle && 'mt-3'} w-full ${
          !isLastSibling && 'border-b-[0.5px]'
        } border-[#C6C6C8] dark:border-dark-divider dark:border-dark-divider sm:border-none`}
      >
        <div className="flex overflow-x-auto scrollbar-none px-2 sm:px-0">
          <div
            className={`flex flex-nowrap flex-row space-s-2 sm:space-s-6 px-2 sm:px-0 !pb-0`}
          >
            {Array.from({ length: 10 }).map((_, i) => {
              return (
                <Skeleton.Button
                  key={i}
                  active={isActive}
                  shape={shape}
                  block={true}
                  className="!w-[90px] sm:!h-[36px] !rounded-full sm:!rounded-[10px] dark:bg-dark-medium"
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export interface CategoriesLargeListItemParameters {
  backgroundImage: string | null;
  icon: string | null;
}

export const RepeatedCuratedListGridItem = React.forwardRef<
  HTMLDivElement,
  BaseComponentProps
>(({ component, context }, ref) => {
  const { getParameterValue, content } =
    useComponentData<CategoriesLargeListItemParameters>(component, context);

  const icon = getParameterValue('icon');

  return (
    <div className="sm:pl-[30px]">
      <ClickableComponentLayout content={content}>
        <div
          ref={ref}
          className="flex space-s-2 sm:space-s-[10px] flex-nowrap items-center p-[4px] pl-[18px] sm:py-[6px] sm:px-[12px] rounded-full sm:rounded-[11px] sm:bg-[#F2F2F6] border sm:border-[2px] border-primary dark:border-primary-dark text-primary dark:text-primary-dark dark:bg-[#1C1C1E]"
        >
          <div
            className={`rounded-full  h-[34px] sm:h-[auto] w-[34px] sm:w-[32px] bg-[#F2F2F6] dark:bg-[#2E2E30] flex justify-center items-center p-2 sm:p-0`}
          >
            <img
              src={MediaHelpers.getPublicMediaById(icon)}
              alt=""
              className="w-[20px] h-[20.92px] sm:w-full sm:h-[30px]"
            />
          </div>
          <div className="text-[15px] sm:text-[18px] whitespace-nowrap inline-block w-fit">
            {content?.title}
          </div>
        </div>
      </ClickableComponentLayout>
    </div>
  );
});
